import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { 
  ArrowLeft, 
  ArrowRight,
  FileText, 
  Variable,
  Eye,
  Send,
  Save,
  CheckCircle,
  AlertCircle,
  Plus,
  FolderOpen,
  Users,
  FileSignature,
  Database,
  RefreshCw,
  Sparkles
} from 'lucide-react';
import api from '../../../lib/api';
import { ContractViewer } from '../../../components/contracts/ContractViewer';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, Button, Badge } from '../../../components/ui';
import { PageHeader, ContextualHelp } from '../../../components/help';
import { contractsHelp } from '../../../config/helpContent';
import { usePreviewResolution } from '../../../hooks/useSystemFields';

interface ContractTemplate {
  id: string;
  name: string;
  category: string;
  content: string;
  variables: {
    name: string;
    type: string;
    required: boolean;
    defaultValue?: string;
  }[];
  requiredSigners: {
    role: string;
    order: number;
    optional: boolean;
  }[];
}

interface TokenizableAsset {
  id: string;
  name: string;
  category: string;
}

interface ContractFormData {
  templateId: string;
  variables: Record<string, string>;
  assetId?: string;
}

const STEPS = ['template', 'variables', 'preview'] as const;
type Step = typeof STEPS[number];

const CATEGORY_LABELS: Record<string, string> = {
  CESSAO: 'Cessão',
  CUSTODIA: 'Custódia',
  ADESAO: 'Adesão',
  DISTRIBUICAO: 'Distribuição',
  GESTAO: 'Gestão',
};

const CATEGORY_COLORS: Record<string, { bg: string; text: string }> = {
  CESSAO: { bg: 'bg-blue-500/20', text: 'text-blue-400' },
  CUSTODIA: { bg: 'bg-purple-500/20', text: 'text-purple-400' },
  ADESAO: { bg: 'bg-green-500/20', text: 'text-green-400' },
  DISTRIBUICAO: { bg: 'bg-orange-500/20', text: 'text-orange-400' },
  GESTAO: { bg: 'bg-surface-700', text: 'text-surface-300' },
};

const SIGNER_ROLE_LABELS: Record<string, { label: string; icon: string }> = {
  ORIGINADOR: { label: 'Originador', icon: '🏢' },
  TENANT: { label: 'Empresa', icon: '🏛️' },
  CUSTODIANTE: { label: 'Custodiante', icon: '🔐' },
  INVESTIDOR: { label: 'Investidor', icon: '💰' },
  TESTEMUNHA: { label: 'Testemunha', icon: '👁️' },
  REPRESENTANTE: { label: 'Representante', icon: '👤' },
};

export function ContractCreate() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedTemplateId = searchParams.get('templateId');
  const preselectedAssetId = searchParams.get('assetId');

  const [currentStep, setCurrentStep] = useState<Step>('template');
  const [selectedTemplate, setSelectedTemplate] = useState<ContractTemplate | null>(null);
  const [useRealData, setUseRealData] = useState(false);
  const [realDataPreview, setRealDataPreview] = useState<string | null>(null);
  const [unresolvedFields, setUnresolvedFields] = useState<string[]>([]);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  const { preview: previewResolution } = usePreviewResolution();

  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm<ContractFormData>({
    defaultValues: {
      templateId: preselectedTemplateId || '',
      variables: {},
      assetId: preselectedAssetId || '',
    },
  });

  const variables = watch('variables');
  const templateId = watch('templateId');

  const { data: templates, isLoading: loadingTemplates } = useQuery({
    queryKey: ['contract-templates-active'],
    queryFn: async () => {
      const response = await api.get<ContractTemplate[]>('/contracts/templates?isActive=true');
      return response.data;
    },
  });

  const { data: assets } = useQuery({
    queryKey: ['tokenizable-assets'],
    queryFn: async () => {
      const response = await api.get<TokenizableAsset[]>('/tokenization/assets');
      return response.data;
    },
  });

  const { data: templateDetails } = useQuery({
    queryKey: ['contract-template', templateId],
    queryFn: async () => {
      const response = await api.get<ContractTemplate>(`/contracts/templates/${templateId}`);
      return response.data;
    },
    enabled: !!templateId,
  });

  useEffect(() => {
    if (templateDetails) {
      setSelectedTemplate(templateDetails);
      const defaultVars: Record<string, string> = {};
      templateDetails.variables.forEach(v => {
        defaultVars[v.name] = v.defaultValue || '';
      });
      setValue('variables', defaultVars);
    }
  }, [templateDetails, setValue]);

  useEffect(() => {
    if (preselectedTemplateId) {
      setValue('templateId', preselectedTemplateId);
    }
  }, [preselectedTemplateId, setValue]);

  const previewContent = useMemo(() => {
    if (!selectedTemplate) return '';
    let content = selectedTemplate.content;
    Object.entries(variables).forEach(([key, value]) => {
      const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
      content = content.replace(regex, value || `[${key}]`);
    });
    return content;
  }, [selectedTemplate, variables]);

  const allRequiredFilled = useMemo(() => {
    if (!selectedTemplate) return false;
    return selectedTemplate.variables
      .filter(v => v.required)
      .every(v => variables[v.name]?.trim());
  }, [selectedTemplate, variables]);

  // Função para carregar preview com dados reais
  const loadRealDataPreview = async () => {
    if (!selectedTemplate) return;
    
    setIsLoadingPreview(true);
    try {
      // Primeiro aplica as variáveis personalizadas
      let contentWithVars = selectedTemplate.content;
      Object.entries(variables).forEach(([key, value]) => {
        const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
        contentWithVars = contentWithVars.replace(regex, value || `[${key}]`);
      });

      // Depois resolve os campos de sistema com dados reais
      const result = await previewResolution(contentWithVars, {
        assetId: watch('assetId') || undefined,
      });
      
      setRealDataPreview(result.content);
      setUnresolvedFields(result.unresolvedFields);
    } catch (error) {
      console.error('Erro ao carregar preview com dados reais:', error);
      setRealDataPreview(null);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  // Carrega preview com dados reais quando ativa a opção
  useEffect(() => {
    if (useRealData && currentStep === 'preview') {
      loadRealDataPreview();
    }
  }, [useRealData, currentStep, selectedTemplate, variables]);

  const createMutation = useMutation({
    mutationFn: async (data: ContractFormData) => {
      const response = await api.post('/contracts', data);
      return response.data;
    },
    onSuccess: (data) => {
      navigate(`/admin/contracts/${data.id}`);
    },
  });

  const createAndSendMutation = useMutation({
    mutationFn: async (data: ContractFormData) => {
      const response = await api.post('/contracts', data);
      await api.post(`/contracts/${response.data.id}/send`);
      return response.data;
    },
    onSuccess: (data) => {
      navigate(`/admin/contracts/${data.id}`);
    },
  });

  const handleNext = () => {
    const currentIndex = STEPS.indexOf(currentStep);
    if (currentIndex < STEPS.length - 1) {
      setCurrentStep(STEPS[currentIndex + 1]);
    }
  };

  const handleBack = () => {
    const currentIndex = STEPS.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(STEPS[currentIndex - 1]);
    }
  };

  const onSubmit = (data: ContractFormData) => {
    createMutation.mutate(data);
  };

  const handleCreateAndSend = () => {
    const data = {
      templateId: watch('templateId'),
      variables: watch('variables'),
      assetId: watch('assetId') || undefined,
    };
    createAndSendMutation.mutate(data);
  };

  const isLoading = createMutation.isPending || createAndSendMutation.isPending;

  return (
    <div className="min-h-screen p-6">
      {/* Back Button */}
      <div className="flex items-center gap-4 mb-2">
        <button
          onClick={() => navigate('/admin/contracts')}
          className="p-2 hover:bg-surface-800 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-surface-400" />
        </button>
      </div>

      {/* Header with Help */}
      <PageHeader
        title="Novo Contrato"
        description={`Passo ${STEPS.indexOf(currentStep) + 1} de ${STEPS.length} - Crie um contrato a partir de um template existente.`}
        icon={<FileSignature className="w-6 h-6 text-primary-400" />}
        module="Contratos Digitais"
        helpContent={contractsHelp.create.helpContent}
      />

      <Card className="mb-6">
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            {STEPS.map((step, index) => {
              const isActive = step === currentStep;
              const isCompleted = STEPS.indexOf(currentStep) > index;
              const stepLabels = { template: 'Template', variables: 'Variáveis', preview: 'Preview' };
              const stepIcons = { template: FileText, variables: Variable, preview: Eye };
              const StepIcon = stepIcons[step];
              
              return (
                <div key={step} className="flex items-center">
                  <div className={`flex items-center gap-2 ${
                    isActive ? 'text-primary-400' : isCompleted ? 'text-green-400' : 'text-surface-500'
                  }`}>
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      isActive ? 'bg-primary-500/20' : isCompleted ? 'bg-green-500/20' : 'bg-surface-800'
                    }`}>
                      {isCompleted ? <CheckCircle className="w-5 h-5" /> : <StepIcon className="w-5 h-5" />}
                    </div>
                    <span className="text-sm font-medium">{stepLabels[step]}</span>
                  </div>
                  {index < STEPS.length - 1 && (
                    <div className={`w-24 h-0.5 mx-4 ${isCompleted ? 'bg-green-500' : 'bg-surface-700'}`} />
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <form onSubmit={handleSubmit(onSubmit)}>
        {currentStep === 'template' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-surface-100">
                <FileText className="w-5 h-5 text-primary-400" />
                Selecione um Template
              </CardTitle>
              <CardDescription className="text-surface-400">
                Escolha o modelo de contrato que deseja utilizar
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingTemplates ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto mb-4" />
                  <p className="text-surface-400">Carregando templates...</p>
                </div>
              ) : !templates || templates.length === 0 ? (
                <div className="text-center py-12">
                  <FolderOpen className="w-16 h-16 mx-auto mb-4 text-surface-600" />
                  <h3 className="text-lg font-medium text-surface-100 mb-2">Nenhum template disponível</h3>
                  <p className="text-surface-400 mb-6">Você precisa criar um template de contrato antes de gerar novos contratos.</p>
                  <Button type="button" onClick={() => navigate('/admin/contracts/templates/new')} className="gap-2">
                    <Plus className="w-4 h-4" />
                    Criar Template
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {templates.map((template) => {
                    const colors = CATEGORY_COLORS[template.category] || { bg: 'bg-surface-700', text: 'text-surface-300' };
                    return (
                      <label
                        key={template.id}
                        className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                          templateId === template.id
                            ? 'border-primary-500 bg-primary-500/10'
                            : 'border-surface-700 hover:border-surface-600 bg-surface-800/50'
                        }`}
                      >
                        <input type="radio" {...register('templateId', { required: 'Selecione um template' })} value={template.id} className="sr-only" />
                        <div className="flex items-start gap-3">
                          <div className={`p-2 rounded-lg ${colors.bg}`}>
                            <FileText className={`w-6 h-6 ${colors.text}`} />
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-surface-100">{template.name}</p>
                            <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full mt-1 ${colors.bg} ${colors.text}`}>
                              {CATEGORY_LABELS[template.category] || template.category}
                            </span>
                            <p className="text-xs text-surface-500 mt-2">
                              {template.variables?.length || 0} variáveis • {template.requiredSigners?.length || 0} signatários
                            </p>
                          </div>
                          {templateId === template.id && <CheckCircle className="w-5 h-5 text-primary-400" />}
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
              {errors.templateId && (
                <p className="text-sm text-red-400 mt-2 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.templateId.message}
                </p>
              )}
              {assets && assets.length > 0 && (
                <div className="mt-6 pt-6 border-t border-surface-700">
                  <label className="block text-sm font-medium text-surface-300 mb-2">Vincular a um ativo tokenizado (opcional)</label>
                  <select {...register('assetId')} className="w-full px-4 py-3 bg-surface-800 border border-surface-700 rounded-lg text-surface-100 focus:border-primary-500 focus:ring-1 focus:ring-primary-500">
                    <option value="">Nenhum ativo</option>
                    {assets.map((asset) => (
                      <option key={asset.id} value={asset.id}>{asset.name} ({asset.category})</option>
                    ))}
                  </select>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {currentStep === 'variables' && selectedTemplate && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-surface-100">
                <Variable className="w-5 h-5 text-primary-400" />
                Preencha as Variáveis
              </CardTitle>
              <CardDescription className="text-surface-400">Complete os campos dinâmicos do contrato</CardDescription>
            </CardHeader>
            <CardContent>
              {selectedTemplate.variables.length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-400" />
                  <p className="text-surface-200">Este template não possui variáveis para preencher.</p>
                  <p className="text-sm text-surface-500 mt-2">Você pode avançar para o preview.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {selectedTemplate.variables.map((variable) => (
                    <div key={variable.name} className="space-y-2">
                      <label className="block text-sm font-medium text-surface-200">
                        {variable.name.replace(/_/g, ' ')}
                        {variable.required && <span className="text-red-400 ml-1">*</span>}
                      </label>
                      <input
                        type={variable.type === 'number' ? 'number' : variable.type === 'date' ? 'date' : 'text'}
                        value={variables[variable.name] || ''}
                        onChange={(e) => setValue(`variables.${variable.name}`, e.target.value)}
                        placeholder={variable.defaultValue || `Digite ${variable.name.replace(/_/g, ' ')}`}
                        className="w-full px-4 py-3 bg-surface-800 border border-surface-700 rounded-lg text-surface-100 placeholder-surface-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                      />
                      <p className="text-xs text-surface-500">
                        Tipo: {variable.type === 'string' ? 'Texto' : variable.type === 'number' ? 'Número' : variable.type === 'date' ? 'Data' : variable.type === 'currency' ? 'Moeda' : variable.type}
                      </p>
                    </div>
                  ))}
                </div>
              )}
              {!allRequiredFilled && selectedTemplate.variables.length > 0 && (
                <div className="mt-4 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                  <p className="text-sm text-yellow-300">Preencha todas as variáveis obrigatórias para continuar.</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {currentStep === 'preview' && selectedTemplate && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-surface-100">
                      <Eye className="w-5 h-5 text-primary-400" />
                      Preview do Contrato
                    </CardTitle>
                    <CardDescription className="text-surface-400">Revise o contrato antes de criar</CardDescription>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setUseRealData(!useRealData)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                        useRealData 
                          ? 'bg-primary-500/20 text-primary-400 border border-primary-500/50' 
                          : 'bg-surface-800 text-surface-400 border border-surface-700 hover:border-surface-600'
                      }`}
                    >
                      <Database className="w-4 h-4" />
                      <span className="text-sm font-medium">Dados Reais</span>
                    </button>
                    {useRealData && (
                      <button
                        type="button"
                        onClick={loadRealDataPreview}
                        disabled={isLoadingPreview}
                        className="p-2 bg-surface-800 hover:bg-surface-700 rounded-lg transition-colors"
                        title="Recarregar preview"
                      >
                        <RefreshCw className={`w-4 h-4 text-surface-400 ${isLoadingPreview ? 'animate-spin' : ''}`} />
                      </button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Info sobre modo de preview */}
                {useRealData && (
                  <div className="mb-4 p-3 bg-primary-500/10 border border-primary-500/30 rounded-lg flex items-start gap-3">
                    <Sparkles className="w-5 h-5 text-primary-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm text-primary-300 font-medium">Preview com Dados Reais</p>
                      <p className="text-xs text-surface-400 mt-1">
                        Os campos de sistema (como {"{{usuario.nome}}"}, {"{{tenant.cnpj}}"}) estão sendo preenchidos com dados reais do banco de dados.
                      </p>
                    </div>
                  </div>
                )}

                {/* Aviso de campos não resolvidos */}
                {useRealData && unresolvedFields.length > 0 && (
                  <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm text-yellow-300 font-medium">Campos não resolvidos</p>
                      <p className="text-xs text-surface-400 mt-1">
                        Os seguintes campos não puderam ser preenchidos (contexto ausente ou dados não encontrados):
                      </p>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {unresolvedFields.map(field => (
                          <span key={field} className="px-2 py-0.5 text-xs bg-yellow-500/20 text-yellow-400 rounded">
                            {`{{${field}}}`}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                <div className="border border-surface-700 rounded-xl overflow-hidden">
                  {isLoadingPreview ? (
                    <div className="h-96 flex items-center justify-center bg-surface-800/50">
                      <div className="text-center">
                        <RefreshCw className="w-8 h-8 text-primary-400 animate-spin mx-auto mb-3" />
                        <p className="text-surface-400">Carregando preview com dados reais...</p>
                      </div>
                    </div>
                  ) : (
                    <ContractViewer 
                      content={useRealData && realDataPreview ? realDataPreview : previewContent} 
                      variables={variables} 
                      status="DRAFT" 
                      className="h-96" 
                    />
                  )}
                </div>
              </CardContent>
            </Card>
            {selectedTemplate.requiredSigners.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-surface-100">
                    <Users className="w-5 h-5 text-green-400" />
                    Signatários Necessários
                  </CardTitle>
                  <CardDescription className="text-surface-400">Pessoas que precisarão assinar este contrato</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {selectedTemplate.requiredSigners.map((signer, index) => {
                      const roleInfo = SIGNER_ROLE_LABELS[signer.role] || { label: signer.role, icon: '👤' };
                      return (
                        <div key={index} className="flex items-center gap-4 p-4 bg-surface-800/50 rounded-xl border border-surface-700">
                          <div className="w-10 h-10 flex items-center justify-center bg-green-500/20 text-green-400 rounded-xl font-bold">{signer.order + 1}</div>
                          <div className="flex-1">
                            <span className="font-medium text-surface-100">{roleInfo.icon} {roleInfo.label}</span>
                          </div>
                          {signer.optional && <Badge variant="default" className="text-xs">Opcional</Badge>}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        <div className="flex items-center justify-between mt-6">
          <Button type="button" variant="ghost" onClick={handleBack} disabled={currentStep === 'template'} className="gap-2">
            <ArrowLeft className="w-4 h-4" />
            Voltar
          </Button>
          <div className="flex items-center gap-3">
            {currentStep === 'preview' ? (
              <>
                <Button type="submit" variant="outline" disabled={isLoading} className="gap-2">
                  <Save className="w-4 h-4" />
                  Salvar Rascunho
                </Button>
                <Button type="button" onClick={handleCreateAndSend} disabled={isLoading} className="gap-2">
                  <Send className="w-4 h-4" />
                  Criar e Enviar
                </Button>
              </>
            ) : (
              <Button
                type="button"
                onClick={handleNext}
                disabled={(currentStep === 'template' && !templateId) || (currentStep === 'variables' && !allRequiredFilled && (selectedTemplate?.variables?.length ?? 0) > 0)}
                className="gap-2"
              >
                Próximo
                <ArrowRight className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </form>

      {(createMutation.isError || createAndSendMutation.isError) && (
        <div className="fixed bottom-6 right-6 p-4 bg-red-500/20 border border-red-500/50 rounded-xl flex items-center gap-3 shadow-xl">
          <AlertCircle className="w-5 h-5 text-red-400" />
          <p className="text-red-300">Erro ao criar contrato. Tente novamente.</p>
        </div>
      )}

      {/* Contextual Help FAQs */}
      <ContextualHelp faqs={contractsHelp.faqs} />
    </div>
  );
}
